import OAuth2ImplicitGrantAuthenticator from 'ember-simple-auth/authenticators/oauth2-implicit-grant';
import type { ImplicitGrantData } from 'ember-simple-auth/authenticators/oauth2-implicit-grant';
import { getOwner } from '@ember/owner';
import { isSsoHandoffPath } from 'tio-employee/utils/sso-handoff';
import { hasPreviewClaim } from 'tio-employee/utils/preview-handoff';
import { tokenRejectionReason } from 'tio-employee/utils/token-check';
import { jwtDecode } from 'tio-common/utils/jwt-decode';
import type SessionService from 'tio-employee/services/session';

function hasExpired(token: string) {
  try {
    const { exp } = jwtDecode<{ exp?: unknown }>(token);
    return typeof exp !== 'number' || exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

export default class Saml extends OAuth2ImplicitGrantAuthenticator {
  /**
   * Refuses persisted SAML state that should not resume as a session.
   *
   * A token that has expired, or cannot be read, is refused on every route. The
   * visitor then boots signed out and signs in through `/login`, rather than
   * booting "authenticated" only for expiry teardown to send them through
   * `/logout`. ESA clears the stored session without an invalidation event, so
   * the refusal itself navigates nowhere.
   *
   * On the SSO landing route a live token is refused too: starting the handoff
   * unauthenticated ensures the new token authentication emits ESA's success
   * event and transitions away from the loading route. Only the boot restore is
   * refused there. ESA also calls `restore` in every open tab when another tab
   * writes session data, and it persists a cleared session when that call
   * rejects — so refusing there would let a tab still sitting on the handoff
   * route wipe the session a successful retry just created elsewhere.
   *
   * @throws when the token has expired, cannot be decoded, or is a landing-preview
   *   grant, and whenever ESA attempts boot-time restoration during an SSO handoff
   */
  async restore(data: Partial<ImplicitGrantData>) {
    if (isSsoHandoffPath() && !this.#session()?.bootRestoreSettled) {
      throw new Error('Skipping persisted SAML session during SSO handoff');
    }

    if (data.access_token && hasExpired(data.access_token)) {
      throw new Error('Skipping persisted SAML session without a live token');
    }

    if (data.access_token && hasPreviewClaim(data.access_token)) {
      throw new Error('Skipping persisted SAML session holding a preview grant');
    }

    // Persisted sessions contain fewer fields than ESA's declared grant type.
    return super.restore(data as ImplicitGrantData);
  }

  #session() {
    return getOwner(this)?.lookup('service:session') as SessionService | undefined;
  }

  /**
   * @throws when the token is a landing-preview grant (only `authenticator:preview`
   *   may seat one, and never into a persisted session) or the API refuses it
   */
  async authenticate(hash: { access_token: string }) {
    if (hash.access_token && hasPreviewClaim(hash.access_token)) {
      throw new Error('Could not authenticate using SAML: preview grant');
    }
    const reason = await tokenRejectionReason(hash.access_token, this.#session()?.publicKey);
    if (reason) {
      throw new Error(`Could not authenticate using SAML: ${reason}`);
    }
    // @ts-expect-error: ESA has types and this now complains
    return super.authenticate(hash);
  }
}
